import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Json } from '@/integrations/supabase/types';

export interface Product {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  sku: string | null;
  base_price: number;
  currency: string;
  tier: string | null;
  category: string | null;
  is_active: boolean;
  metadata: Json;
  created_at: string;
  updated_at: string;
}

export interface PricingRule {
  id: string;
  tenant_id: string;
  product_id: string | null;
  name: string;
  rule_type: string;
  condition: Json;
  value: number;
  value_type: string;
  priority: number;
  is_active: boolean;
  valid_from: string | null;
  valid_until: string | null;
  metadata: Json;
  created_at: string;
  updated_at: string;
}

export interface DealLineItem {
  id: string;
  tenant_id: string;
  deal_id: string;
  product_id: string | null;
  name: string;
  description: string | null;
  quantity: number;
  unit_price: number;
  discount_percent: number;
  discount_amount: number;
  total_price: number;
  ai_suggested_price: number | null;
  ai_price_reasoning: string | null;
  metadata: Json;
  created_at: string;
  updated_at: string;
  product?: Product;
}

// Products Hooks
export function useProducts() {
  return useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('name');
      
      if (error) throw error;
      return data as Product[];
    },
  });
}

export function useCreateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (product: { name: string; description?: string | null; sku?: string | null; base_price: number; currency: string; category?: string | null; tier?: string | null; is_active: boolean; metadata?: Json }) => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('tenant_id')
        .single();

      if (!profile?.tenant_id) throw new Error('No tenant found');

      const { data, error } = await supabase
        .from('products')
        .insert({ 
          name: product.name,
          description: product.description,
          sku: product.sku,
          base_price: product.base_price,
          currency: product.currency,
          category: product.category,
          tier: product.tier,
          is_active: product.is_active,
          metadata: product.metadata || {},
          tenant_id: profile.tenant_id 
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Product created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create product: ' + error.message);
    },
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; name?: string; description?: string | null; sku?: string | null; base_price?: number; currency?: string; category?: string | null; tier?: string | null; is_active?: boolean }) => {
      const { data, error } = await supabase
        .from('products')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Product updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update product: ' + error.message);
    },
  });
}

export function useDeleteProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Product deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete product: ' + error.message);
    },
  });
}

export function useDuplicateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (productId: string) => {
      // Fetch the original product
      const { data: original, error: fetchError } = await supabase
        .from('products')
        .select('*')
        .eq('id', productId)
        .single();

      if (fetchError || !original) throw new Error('Product not found');

      // Create the duplicate
      const { data: duplicate, error: createError } = await supabase
        .from('products')
        .insert({
          tenant_id: original.tenant_id,
          name: `${original.name} (Copy)`,
          description: original.description,
          sku: original.sku ? `${original.sku}-COPY` : null,
          base_price: original.base_price,
          currency: original.currency,
          tier: original.tier,
          category: original.category,
          is_active: false, // Start as inactive
          metadata: original.metadata,
        })
        .select()
        .single();

      if (createError || !duplicate) throw createError || new Error('Failed to create duplicate');

      // Fetch and duplicate pricing rules
      const { data: rules } = await supabase
        .from('pricing_rules')
        .select('*')
        .eq('product_id', productId);

      if (rules && rules.length > 0) {
        const duplicateRules = rules.map(rule => ({
          tenant_id: rule.tenant_id,
          product_id: duplicate.id,
          name: rule.name,
          rule_type: rule.rule_type,
          condition: rule.condition,
          value: rule.value,
          value_type: rule.value_type,
          priority: rule.priority,
          is_active: rule.is_active,
          valid_from: rule.valid_from,
          valid_until: rule.valid_until,
          metadata: rule.metadata,
        }));

        await supabase.from('pricing_rules').insert(duplicateRules);
      }

      return duplicate;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['pricing-rules'] });
      toast.success('Product duplicated successfully');
    },
    onError: (error) => {
      toast.error('Failed to duplicate product: ' + error.message);
    },
  });
}

export function useBulkUpdateProducts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ids, updates }: { ids: string[]; updates: { is_active?: boolean; category?: string | null } }) => {
      const { error } = await supabase
        .from('products')
        .update(updates)
        .in('id', ids);

      if (error) throw error;
      return { count: ids.length };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success(`${data.count} products updated successfully`);
    },
    onError: (error) => {
      toast.error('Failed to update products: ' + error.message);
    },
  });
}

export function useBulkDeleteProducts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      // First delete associated pricing rules
      await supabase
        .from('pricing_rules')
        .delete()
        .in('product_id', ids);

      // Then delete the products
      const { error } = await supabase
        .from('products')
        .delete()
        .in('id', ids);

      if (error) throw error;
      return { count: ids.length };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['pricing-rules'] });
      toast.success(`${data.count} products deleted successfully`);
    },
    onError: (error) => {
      toast.error('Failed to delete products: ' + error.message);
    },
  });
}

// Pricing Rules Hooks
export function usePricingRules(productId?: string) {
  return useQuery({
    queryKey: ['pricing-rules', productId],
    queryFn: async () => {
      let query = supabase
        .from('pricing_rules')
        .select('*')
        .order('priority', { ascending: false });

      if (productId) {
        query = query.eq('product_id', productId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as PricingRule[];
    },
  });
}

export function useCreatePricingRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (rule: { product_id: string | null; name: string; rule_type: string; value: number; value_type: string; condition: Json; priority: number; is_active: boolean; valid_from: string | null; valid_until: string | null; metadata?: Json }) => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('tenant_id')
        .single();

      if (!profile?.tenant_id) throw new Error('No tenant found');

      const { data, error } = await supabase
        .from('pricing_rules')
        .insert({ 
          product_id: rule.product_id,
          name: rule.name,
          rule_type: rule.rule_type,
          value: rule.value,
          value_type: rule.value_type,
          condition: rule.condition,
          priority: rule.priority,
          is_active: rule.is_active,
          valid_from: rule.valid_from,
          valid_until: rule.valid_until,
          metadata: rule.metadata || {},
          tenant_id: profile.tenant_id 
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-rules'] });
      toast.success('Pricing rule created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create pricing rule: ' + error.message);
    },
  });
}

export function useDeletePricingRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('pricing_rules')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-rules'] });
      toast.success('Pricing rule deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete pricing rule: ' + error.message);
    },
  });
}

// Deal Line Items Hooks
export function useDealLineItems(dealId: string) {
  return useQuery({
    queryKey: ['deal-line-items', dealId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('deal_line_items')
        .select('*, product:products(*)')
        .eq('deal_id', dealId)
        .order('created_at');

      if (error) throw error;
      return data as DealLineItem[];
    },
    enabled: !!dealId,
  });
}

export function useCreateDealLineItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (item: { deal_id: string; product_id: string | null; name: string; description: string | null; quantity: number; unit_price: number; discount_percent: number; discount_amount: number; total_price: number; ai_suggested_price: number | null; ai_price_reasoning: string | null; metadata?: Json }) => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('tenant_id')
        .single();

      if (!profile?.tenant_id) throw new Error('No tenant found');

      const { data, error } = await supabase
        .from('deal_line_items')
        .insert({ 
          deal_id: item.deal_id,
          product_id: item.product_id,
          name: item.name,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount_percent: item.discount_percent,
          discount_amount: item.discount_amount,
          total_price: item.total_price,
          ai_suggested_price: item.ai_suggested_price,
          ai_price_reasoning: item.ai_price_reasoning,
          metadata: item.metadata || {},
          tenant_id: profile.tenant_id 
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['deal-line-items', variables.deal_id] });
      toast.success('Line item added successfully');
    },
    onError: (error) => {
      toast.error('Failed to add line item: ' + error.message);
    },
  });
}

export function useUpdateDealLineItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, deal_id, ...updates }: { id: string; deal_id: string; ai_suggested_price?: number | null; ai_price_reasoning?: string | null }) => {
      const { data, error } = await supabase
        .from('deal_line_items')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { ...data, deal_id };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['deal-line-items', data.deal_id] });
      toast.success('Line item updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update line item: ' + error.message);
    },
  });
}

export function useDeleteDealLineItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, deal_id }: { id: string; deal_id: string }) => {
      const { error } = await supabase
        .from('deal_line_items')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { deal_id };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['deal-line-items', data.deal_id] });
      toast.success('Line item removed successfully');
    },
    onError: (error) => {
      toast.error('Failed to remove line item: ' + error.message);
    },
  });
}

// Calculate price with applicable rules
export function calculatePriceWithRules(
  basePrice: number,
  quantity: number,
  rules: PricingRule[]
): { finalPrice: number; appliedRules: PricingRule[] } {
  const activeRules = rules
    .filter(r => r.is_active)
    .sort((a, b) => b.priority - a.priority);

  let finalPrice = basePrice * quantity;
  const appliedRules: PricingRule[] = [];

  for (const rule of activeRules) {
    const condition = rule.condition as { min_quantity?: number; max_quantity?: number } | null;
    
    if (condition) {
      if (condition.min_quantity && quantity < condition.min_quantity) continue;
      if (condition.max_quantity && quantity > condition.max_quantity) continue;
    }

    if (rule.value_type === 'percentage') {
      if (rule.rule_type === 'discount') {
        finalPrice = finalPrice * (1 - rule.value / 100);
      } else if (rule.rule_type === 'markup') {
        finalPrice = finalPrice * (1 + rule.value / 100);
      }
    } else {
      if (rule.rule_type === 'discount') {
        finalPrice = finalPrice - rule.value;
      } else if (rule.rule_type === 'markup') {
        finalPrice = finalPrice + rule.value;
      }
    }

    appliedRules.push(rule);
  }

  return { finalPrice: Math.max(0, finalPrice), appliedRules };
}